import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Plus, Package, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'

export function PackageTemplatesPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [formError, setFormError] = useState('')

  const { data, isLoading } = useQuery({ queryKey: ['package-templates'], queryFn: () => api.getPackageTemplates() })
  const items = data?.items || []

  const create = useMutation({
    mutationFn: api.createPackageTemplate,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['package-templates'] }); setShowForm(false); setFormError('') },
    // OPT-040：模板数超档位上限 → 409 提示升级
    onError: (e) => {
      const code = (e as Error & { code?: string }).code
      setFormError(code === 'LIMIT_REACHED' ? t('errors.limitReached') : e instanceof Error ? e.message : t('common.failed'))
    },
  })
  const del = useMutation({
    mutationFn: api.deletePackageTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['package-templates'] }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t('packages.templatesTitle')}</h1>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t('packages.newTemplate')}
        </Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="p-6 text-center text-slate-500">{t('common.loading')}</CardContent></Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-4">{t('packages.noTemplates')}</p>
          <Button onClick={() => setShowForm(true)}><Plus className="h-4 w-4 mr-1" /> {t('packages.addFirst')}</Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((tpl) => (
            <Card key={tpl.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold">{tpl.name}</div>
                  <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => { if (window.confirm(t('common.confirmDelete') || 'Delete?')) del.mutate(tpl.id) }}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>{t('packages.total')}: {tpl.totalSessions}</p>
                  {tpl.price != null && <p>¥{tpl.price}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) setShowForm(false) }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('packages.newTemplate')}</DialogTitle></DialogHeader>
          <TemplateForm onClose={() => setShowForm(false)} onSubmit={(body) => create.mutate(body)} />
          {formError && <p className="text-sm text-red-600">{formError}</p>}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function TemplateForm({ onClose, onSubmit }: { onClose: () => void; onSubmit: (body: { name: string; totalSessions: number; price?: number }) => void }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [total, setTotal] = useState('10')
  const [price, setPrice] = useState('')

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; onSubmit({ name: name.trim(), totalSessions: parseInt(total, 10) || 10, price: price ? parseInt(price, 10) : undefined }) }}>
      <div className="space-y-3">
        <div><Label htmlFor="tpl-name">{t('packages.form.name')}</Label><Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('packages.form.namePlaceholder')} required autoFocus /></div>
        <div><Label htmlFor="tpl-sessions">{t('packages.form.totalSessions')}</Label><Input id="tpl-sessions" type="number" min={1} value={total} onChange={(e) => setTotal(e.target.value)} required /></div>
        <div><Label htmlFor="tpl-price">{t('packages.form.price')}</Label><Input id="tpl-price" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} placeholder={t('packages.form.pricePlaceholder')} /></div>
      </div>
      <DialogFooter className="mt-4">
        <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="submit">{t('common.save')}</Button>
      </DialogFooter>
    </form>
  )
}
